import { useEffect, useRef, useState } from 'react'
import { Car, Building2, Calendar, Tags, Layers, BarChart3, FileText, FileSpreadsheet, ChevronDown } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha } from './LancamentoDepositos'

interface Mes {
  ano: number; mes: number; percurso: number
  litrosComb: number; kmlComb: number | null; valorComb: number
  litrosArla: number; kmlArla: number | null; valorArla: number
  pctLitros: number | null; pctValor: number | null
}
interface Veiculo { veicod: string; veipla: string; tipo: string; modelo: string; meses: Mes[] }
interface Opcao { cod: string; des: string }

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
const fmt = (v: number, casas: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })
// Igual ao ERP: fora da faixa sai como ***,**.
const fmtFaixa = (v: number | null) => (v == null ? '—' : v > 999.99 ? '***,**' : fmt(v, 2))

// Cartão do total geral: faixa colorida identifica o grupo, números em tinta escura.
export function CartaoTotal({ titulo, cor, itens }: { titulo: string; cor: string; itens: { rotulo: string; valor: string }[] }) {
  return (
    <div style={{ background: '#fff', borderRadius: 10, borderTop: `5px solid ${cor}`, padding: '0.6rem 0.8rem 0.7rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.68rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.45rem' }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: cor }} /> {titulo}
      </div>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {itens.map(i => (
          <div key={i.rotulo}>
            <div style={{ fontSize: '0.66rem', color: '#94a3b8', fontWeight: 700 }}>{i.rotulo}</div>
            <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{i.valor}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// "Todos" ou escolha de vários itens, com busca.
export function SeletorMultiplo({ opcoes, selecionados, onChange, placeholderBusca = 'Buscar modelo...' }: { opcoes: Opcao[]; selecionados: string[]; onChange: (s: string[]) => void; placeholderBusca?: string }) {
  const [aberto, setAberto] = useState(false)
  const [busca, setBusca] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!aberto) return
    const fechar = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false) }
    document.addEventListener('mousedown', fechar)
    return () => document.removeEventListener('mousedown', fechar)
  }, [aberto])

  const termo = busca.trim().toUpperCase()
  const filtradas = termo ? opcoes.filter(o => o.des.toUpperCase().includes(termo) || o.cod.toUpperCase().includes(termo)) : opcoes
  const alternar = (cod: string) => onChange(selecionados.includes(cod) ? selecionados.filter(c => c !== cod) : [...selecionados, cod])
  // Estilo próprio: dentro de .field (telas de Vendas) herdaria label em maiúsculas/centralizado e checkbox 100%.
  const estiloLinha: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '0.5rem', padding: '0.3rem 0.4rem', fontSize: '0.82rem',
    cursor: 'pointer', textTransform: 'none', letterSpacing: 'normal', color: '#1f2937', textAlign: 'left', margin: 0, width: '100%',
  }
  const estiloCheck: React.CSSProperties = { width: 'auto', height: 'auto', margin: 0, flex: '0 0 auto', padding: 0 }
  const resumo = selecionados.length === 0 ? 'Todos'
    : selecionados.length === 1 ? (opcoes.find(o => o.cod === selecionados[0])?.des || selecionados[0])
    : `${selecionados.length} selecionados`

  return (
    <div ref={ref} style={{ position: 'relative', maxWidth: 280 }}>
      <button type="button" onClick={() => setAberto(a => !a)} style={{
        width: '100%', textAlign: 'left', padding: '0.45rem 0.7rem', borderRadius: 8, border: '1px solid #d1d5db',
        background: '#fff', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resumo}</span>
        <ChevronDown size={15} color="#64748b" />
      </button>
      {aberto && (
        <div style={{
          position: 'absolute', zIndex: 20, top: 'calc(100% + 4px)', left: 0, width: 320, background: '#fff',
          border: '1px solid #d1d5db', borderRadius: 8, boxShadow: '0 10px 24px -8px rgba(15,23,42,0.25)', padding: '0.5rem',
        }}>
          <input autoFocus type="text" value={busca} onChange={e => setBusca(e.target.value)} placeholder={placeholderBusca} style={{ width: '100%', marginBottom: '0.4rem' }} />
          <label style={{ ...estiloLinha, fontWeight: 700, borderBottom: '1px solid #eef2f7' }}>
            <input type="checkbox" style={estiloCheck} checked={selecionados.length === 0} onChange={() => onChange([])} /> Todos
          </label>
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {filtradas.map(o => (
              <label key={o.cod} style={{ ...estiloLinha, fontWeight: 400 }}>
                <input type="checkbox" style={estiloCheck} checked={selecionados.includes(o.cod)} onChange={() => alternar(o.cod)} />
                <span style={{ textAlign: 'left' }}>{o.des}</span>
                {o.des !== o.cod && <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: '#94a3b8' }}>{o.cod}</span>}
              </label>
            ))}
            {filtradas.length === 0 && <div style={{ padding: '0.5rem', fontSize: '0.8rem', color: '#94a3b8' }}>Nada encontrado.</div>}
          </div>
        </div>
      )}
    </div>
  )
}

// Consumo mensal de combustível x ARLA32 (GeneXus WOCOMARL → RDPS075).
export default function RelatorioConsumoArla() {
  const { empresas } = useEmpresa()
  const hoje = new Date()
  const [veiculoDe, setVeiculoDe] = useState('')
  const [veiculoAte, setVeiculoAte] = useState('')
  const [modelosSel, setModelosSel] = useState<string[]>([]) // vazio = todos
  const [empresa, setEmpresa] = useState('0')
  const [mesIni, setMesIni] = useState(hoje.getMonth() + 1)
  const [anoIni, setAnoIni] = useState(hoje.getFullYear())
  const [mesFim, setMesFim] = useState(hoje.getMonth() + 1)
  const [anoFim, setAnoFim] = useState(hoje.getFullYear())
  const [tipo, setTipo] = useState('')

  const [tipos, setTipos] = useState<Opcao[]>([])
  const [modelos, setModelos] = useState<Opcao[]>([])
  const [res, setRes] = useState<Veiculo[] | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')

  useEffect(() => {
    api.get('/combustivel/tipos-veiculo').then(r => setTipos(r.data)).catch(() => {})
    api.get('/combustivel/modelos-agrupamento').then(r => setModelos(r.data)).catch(() => {})
  }, [])

  const params = () => ({
    veiculoDe, veiculoAte, modelos: modelosSel.join(','), empresa, tipo,
    inicio: `${anoIni}-${String(mesIni).padStart(2, '0')}`,
    fim: `${anoFim}-${String(mesFim).padStart(2, '0')}`,
  })

  async function gerar() {
    setErro('')
    setCarregando(true)
    try {
      const r = await api.get('/combustivel/relatorios/consumo-arla', { params: params() })
      setRes(r.data)
    } catch (e: any) {
      setRes(null)
      setErro(e?.response?.data?.error || 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function exportar(formato: 'pdf' | 'excel') {
    setErro('')
    setGerando(formato)
    try {
      const r = await api.get(`/combustivel/relatorios/consumo-arla/${formato}`, { params: params(), responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') {
        window.open(url, '_blank')
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = 'consumo-arla-combustivel.xlsx'
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch {
      setErro(formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.')
    } finally {
      setGerando('')
    }
  }

  const seletorMes = (mes: number, setMes: (m: number) => void, ano: number, setAno: (a: number) => void) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <select value={mes} onChange={e => setMes(Number(e.target.value))} style={{ maxWidth: 140 }}>
        {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
      </select>
      <span style={{ color: '#57606f' }}>/</span>
      <input type="number" value={ano} min={2000} max={2100} onChange={e => setAno(Number(e.target.value))} style={{ width: 90 }} />
    </div>
  )

  const th: React.CSSProperties = {
    padding: '0.4rem 0.5rem', fontSize: '0.66rem', fontWeight: 800, color: '#1b3f7b', background: '#e8eef8',
    textAlign: 'right', whiteSpace: 'nowrap', textTransform: 'uppercase',
  }
  const grp = (cor: string): React.CSSProperties => ({
    padding: '0.3rem', fontSize: '0.66rem', fontWeight: 800, color: '#fff', background: cor, textAlign: 'center', textTransform: 'uppercase',
  })
  const td: React.CSSProperties = { padding: '0.35rem 0.5rem', fontSize: '0.78rem', borderBottom: '1px solid #eef2f7', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }

  const cabecalhoTabela = (
    <thead>
      <tr>
        <th colSpan={2} style={grp('#475569')}>Período</th>
        <th colSpan={3} style={grp('#1b3f7b')}>Combustível</th>
        <th colSpan={3} style={grp('#047857')}>ARLA32</th>
        <th colSpan={2} style={grp('#7c3aed')}>ARLA/Combustível em %</th>
      </tr>
      <tr>
        <th style={{ ...th, textAlign: 'left' }}>Ano / Mês</th><th style={th}>Percurso</th>
        <th style={th}>Litros</th><th style={th}>Km/L</th><th style={th}>Valor</th>
        <th style={th}>Litros</th><th style={th}>Km/L</th><th style={th}>Valor</th>
        <th style={th}>% Litros</th><th style={th}>% Valor</th>
      </tr>
    </thead>
  )

  // Total geral: soma percurso/litros/valores; km/l e % sobre os totais, truncados como no ERP.
  const trunc2 = (a: number, b: number) => (b > 0 ? Math.trunc((a / b) * 100 + 1e-9) / 100 : null)
  const total = res && res.length ? (() => {
    const t = { veiculos: res.length, percurso: 0, litrosComb: 0, valorComb: 0, litrosArla: 0, valorArla: 0 }
    for (const v of res) for (const m of v.meses) {
      t.percurso += m.percurso; t.litrosComb += m.litrosComb; t.valorComb += m.valorComb
      t.litrosArla += m.litrosArla; t.valorArla += m.valorArla
    }
    return {
      ...t, kmlComb: trunc2(t.percurso, t.litrosComb), kmlArla: trunc2(t.percurso, t.litrosArla),
      pctLitros: trunc2(t.litrosArla * 100, t.litrosComb), pctValor: trunc2(t.valorArla * 100, t.valorComb),
    }
  })() : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <BarChart3 size={18} color="#fff" />
            </span>
            <div>
              <h2>Consumo ARLA x Combustível</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Consumo mensal de combustível e ARLA32 por veículo</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Veículos" icon={Car}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ maxWidth: 140 }}>
                <LookupSelect endpoint="/erp/veiculos" codKey="veicod" descKey="veipla" tagKey="veicod"
                  codValue={veiculoDe} descValue={veiculoDe} onSelect={cod => setVeiculoDe(cod)} placeholder="Primeiro" />
              </div>
              <span style={{ fontSize: '0.8rem', color: '#57606f' }}>a</span>
              <div style={{ maxWidth: 140 }}>
                <LookupSelect endpoint="/erp/veiculos" codKey="veicod" descKey="veipla" tagKey="veicod"
                  codValue={veiculoAte} descValue={veiculoAte} onSelect={cod => setVeiculoAte(cod)} placeholder="Último" />
              </div>
              {(veiculoDe || veiculoAte) && (
                <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }} onClick={() => { setVeiculoDe(''); setVeiculoAte('') }}>Todos</button>
              )}
            </div>
          </Linha>

          <Linha label="Mod. Agrupamento" icon={Layers}>
            <SeletorMultiplo opcoes={modelos} selecionados={modelosSel} onChange={setModelosSel} />
          </Linha>

          <Linha label="Empresa" icon={Building2}>
            <select value={empresa} onChange={e => setEmpresa(e.target.value)} style={{ maxWidth: 280 }}>
              <option value="0">Todas</option>
              {empresas.map(e => <option key={e.empcod} value={e.empcod}>{e.empcod} — {e.empfan?.trim() || e.empnom?.trim()}</option>)}
            </select>
          </Linha>

          <Linha label="Início" icon={Calendar}>{seletorMes(mesIni, setMesIni, anoIni, setAnoIni)}</Linha>
          <Linha label="Fim" icon={Calendar}>{seletorMes(mesFim, setMesFim, anoFim, setAnoFim)}</Linha>

          <Linha label="Tipo" icon={Tags}>
            <select value={tipo} onChange={e => setTipo(e.target.value)} style={{ maxWidth: 280 }}>
              <option value="">Todos</option>
              {tipos.map(t => <option key={t.cod} value={t.cod}>{t.des}</option>)}
            </select>
          </Linha>

          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
              {erro}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
          <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('pdf')} disabled={!!gerando}>
            <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('excel')} disabled={!!gerando}>
            <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {res.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum consumo de ARLA no período.</div>}
            {res.map(v => (
              <div key={v.veicod} style={{ border: '1px solid #bfdbfe', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                <div style={{ padding: '0.5rem 0.8rem', fontSize: '0.8rem', fontWeight: 700, color: '#1b3f7b', background: '#f5f9fd', display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                  <span>Veículo: {v.veicod}</span><span>Placa: {v.veipla || '—'}</span><span>Tipo: {v.tipo || '—'}</span><span>Modelo: {v.modelo || '—'}</span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    {cabecalhoTabela}
                    <tbody>
                      {v.meses.map(m => (
                        <tr key={`${m.ano}-${m.mes}`}>
                          <td style={{ ...td, textAlign: 'left' }}>{m.ano} / {MESES[m.mes - 1]}</td>
                          <td style={td}>{fmt(m.percurso, 0)}</td>
                          <td style={td}>{fmt(Math.trunc(m.litrosComb), 0)}</td>
                          <td style={td}>{fmtFaixa(m.kmlComb)}</td>
                          <td style={td}>{fmt(m.valorComb, 2)}</td>
                          <td style={td}>{fmt(Math.trunc(m.litrosArla), 0)}</td>
                          <td style={td}>{fmtFaixa(m.kmlArla)}</td>
                          <td style={td}>{fmt(m.valorArla, 2)}</td>
                          <td style={td}>{fmtFaixa(m.pctLitros)}</td>
                          <td style={td}>{fmtFaixa(m.pctValor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            {total && (
              // Painel de indicadores, propositalmente diferente das tabelas por veículo.
              <div style={{ borderRadius: 12, padding: '1rem 1.1rem 1.1rem', background: 'linear-gradient(135deg, #0f2a52, #1b3f7b)', marginTop: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.85rem' }}>
                  <span style={{ color: '#fff', fontSize: '1rem', fontWeight: 800, letterSpacing: '0.02em' }}>TOTAL GERAL</span>
                  <span style={{ color: '#bfdbfe', fontSize: '0.78rem', fontWeight: 600 }}>
                    {total.veiculos} veículo{total.veiculos === 1 ? '' : 's'} · {MESES[mesIni - 1]}/{anoIni} a {MESES[mesFim - 1]}/{anoFim}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.7rem' }}>
                  <CartaoTotal titulo="Período" cor="#475569" itens={[{ rotulo: 'Percurso (km)', valor: fmt(total.percurso, 0) }]} />
                  <CartaoTotal titulo="Combustível" cor="#1b3f7b" itens={[
                    { rotulo: 'Litros', valor: fmt(Math.trunc(total.litrosComb), 0) },
                    { rotulo: 'Km/L', valor: fmtFaixa(total.kmlComb) },
                    { rotulo: 'Valor', valor: `R$ ${fmt(total.valorComb, 2)}` },
                  ]} />
                  <CartaoTotal titulo="ARLA32" cor="#047857" itens={[
                    { rotulo: 'Litros', valor: fmt(Math.trunc(total.litrosArla), 0) },
                    { rotulo: 'Km/L', valor: fmtFaixa(total.kmlArla) },
                    { rotulo: 'Valor', valor: `R$ ${fmt(total.valorArla, 2)}` },
                  ]} />
                  <CartaoTotal titulo="ARLA / Combustível" cor="#7c3aed" itens={[
                    { rotulo: '% Litros', valor: `${fmtFaixa(total.pctLitros)}%` },
                    { rotulo: '% Valor', valor: `${fmtFaixa(total.pctValor)}%` },
                  ]} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
